/** ------------------------------------------------------------------------- *
 * Testimonials Carousel *
 * -------------------------------------------------------------------------- *
 * @module Testimonials
 * @desc This module initializes and creates the JavaScript functionality for the testimonials.
 * It also makes the markup for the buttons since you can't use a carousel without JavaScript
 * anyways. This code is heavily based on the Carousel Prototype by Eric Eggert for W3C. <3
 * Class names to probably edit for your own project:
 * testimonial, visually-hidden
 */
//----------------------------------------------------------------
// INITIAL VARIABLES
let carousel;
let slides;
let index;
let slidenav;
let settings; // Make settings available to all functions
let timer;
let setFocus;
let animationSuspended;
let i = 0;

//----------------------------------------------------------------
// HELPER FUNCTIONS
// Iterates over an array of elements
function forEachElement(elements, fn) {
    for (i = 0; i < elements.length; i++) {
        fn(elements[i], i);
    }
}

// Remove Class
function removeClass(el, className) {
    if (el.classList) {
        el.classList.remove(className);
    } else {
        el.className = el.className.replace(new RegExp(`(^|\\b)${className.split(' ').join('|')}(\\b|$)`, 'gi'), ' ');
    }
}

// Test if element has a specific class
function hasClass(el, className) {
    if (el.classList) {
        return el.classList.contains(className);
    }
    return new RegExp(`(^| )${className}( |$)`, 'gi').test(el.className);
}

//----------------------------------------------------------------
/** ------------- *
 * MODULE EXPORT  *
 * -------------- *
 */

const Testimonials = {
    // Initialization for the carousel
    // Argument: set = an object of settings
    // Possible settings:
    // id <string> ID of the carousel wrapper element (required).
    // slidenav <bool> If true, a list of slides is shown.
    // animate <bool> If true, the slides can be animated.
    // startAnimated <bool> If true, the animation begins immediately.
    //        If false, the animation needs to be initiated by clicking
    //        the play button.
    init(set) {
        settings = set; // Assign settings to the global variable

        // Select the element and the individual slides
        carousel = document.getElementById(settings.id);
        slides = carousel.querySelectorAll('.testimonial');

        carousel.className = 'active carousel';

        // Create unordered list for controls, and attach click events to previous and next slide
        const ctrls = document.createElement('ul');

        ctrls.className = 'controls';
        ctrls.innerHTML = `
          <li>
            <button type="button" class="btn-prev">
              Previous Quote
            </button>
          </li>
          <li>
            <button type="button" class="btn-next">
              Next Quote
            </button>
          </li>
        `;

        ctrls.querySelector('.btn-prev').addEventListener('click', () => {
            Testimonials.prev(true);
        });
        ctrls.querySelector('.btn-next').addEventListener('click', () => {
            Testimonials.next(true);
        });

        carousel.appendChild(ctrls);

        // If the carousel is animated or a slide navigation is requested in the settings,
        // another unordered list that contains those elements is added.
        if (settings.slidenav || settings.animate) {
            slidenav = document.createElement('ul');
            slidenav.className = 'slidenav';

            if (settings.animate) {
                const li = document.createElement('li');
                li.innerHTML = settings.startAnimated
                    ? '<button class="animation-button" data-action="stop"><span class="visually-hidden">Stop Animation </span>⏸</button>'
                    : '<button class="animation-button" data-action="start"><span class="visually-hidden">Start Animation </span>▶</button>';
                slidenav.appendChild(li);
            }

            if (settings.slidenav) {
                forEachElement(slides, (el, slideIndex) => {
                    const li = document.createElement('li');
                    const klass = slideIndex === 0 ? 'class="current" ' : '';
                    const kurrent = slideIndex === 0 ? ' <span class="visually-hiddenn">(Current Item)</span>' : '';

                    li.innerHTML = `
                        <button ${klass}data-slide="${slideIndex}">
                            <span class="visually-hidden">Testimonials</span> ${slideIndex + 1}${kurrent}
                        </button>
                    `;
                    slidenav.appendChild(li);
                });
            }

            slidenav.addEventListener(
                'click',
                (event) => {
                    const button = event.target;
                    if (button.localName === 'button') {
                        if (button.getAttribute('data-slide')) {
                            Testimonials.stop();
                            Testimonials.goto(button.getAttribute('data-slide'), true);
                        } else if (button.getAttribute('data-action') === 'stop') {
                            Testimonials.stop();
                        } else if (button.getAttribute('data-action') === 'start') {
                            Testimonials.start();
                        }
                    }
                },
                true,
            );

            carousel.className = 'active carousel with-slidenav';
            carousel.appendChild(slidenav);
        }

        // Add a live region to announce the slide number when using the previous/next buttons
        const liveregion = document.createElement('div');
        liveregion.setAttribute('aria-live', 'polite');
        liveregion.setAttribute('aria-atomic', 'true');
        liveregion.setAttribute('class', 'liveregion visually-hidden');
        carousel.appendChild(liveregion);

        // After the slide transitioned, remove the in-transition class,
        // if focus should be set, set the tabindex attribute to -1 and focus the slide.
        slides[0].parentNode.addEventListener('transitionend', (event) => {
            const slide = event.target;
            removeClass(slide, 'in-transition');
            if (hasClass(slide, 'current')) {
                if (setFocus) {
                    slide.setAttribute('tabindex', '-1');
                    slide.focus();
                    setFocus = false;
                }
            }
        });

        // When the mouse enters the carousel, suspend the animation.
        carousel.addEventListener('mouseenter', Testimonials.suspend);

        // When the mouse leaves the carousel, and the animation is suspended, start the animation.
        carousel.addEventListener('mouseleave', () => {
            if (animationSuspended) {
                Testimonials.start();
            }
        });

        // When the focus enters the carousel, suspend the animation
        carousel.addEventListener('focusin', (event) => {
            if (!hasClass(event.target, 'slide')) {
                Testimonials.suspend();
            }
        });

        // When the focus leaves the carousel, and the animation is suspended, start the animation
        carousel.addEventListener('focusout', (event) => {
            if (!hasClass(event.target, 'slide') && animationSuspended) {
                Testimonials.start();
            }
        });

        // Set the index (=current slide) to 0 – the first slide
        index = 0;
        Testimonials.goto(index);

        // If the carousel is animated, advance to the next slide after 5s
        if (settings.startAnimated) {
            timer = setTimeout(Testimonials.next, 5000);
        }
    },

    // Function to set a slide as the current slide.
    // Accessibility: Hiding transitioning elements from assistive technologies.
    // The aria-hidden attribute is set to true to hide the item from assistive technologies.
    // When the transition completes, the aria-hidden attribute is removed.
    goto(new_current, setFocusHere, transition, announceItemHere) {
        setFocus = typeof setFocusHere !== 'undefined' ? setFocusHere : false;
        transition = typeof transition !== 'undefined' ? transition : 'none';
        const announceItem = typeof announceItemHere !== 'undefined' ? announceItemHere : false;

        new_current = parseFloat(new_current);

        const length = slides.length;
        let new_next = new_current + 1;
        let new_prev = new_current - 1;

        if (new_next === length) {
            new_next = 0;
        } else if (new_prev < 0) {
            new_prev = length - 1;
        }

        slides.forEach((slide) => {
            slide.className = 'slide';
        });

        slides[new_next].className = `next slide${transition === 'next' ? ' in-transition' : ''}`;
        slides[new_next].setAttribute('aria-hidden', 'true');

        slides[new_prev].className = `prev slide${transition === 'prev' ? ' in-transition' : ''}`;
        slides[new_prev].setAttribute('aria-hidden', 'true');

        slides[new_current].className = 'current slide';
        slides[new_current].removeAttribute('aria-hidden');

        if (announceItem) {
            carousel.querySelector('.liveregion').textContent = `Item ${new_current + 1} of ${slides.length}`;
        }

        // Update the buttons in the slider navigation
        if (settings.slidenav) {
            const buttons = carousel.querySelectorAll('.slidenav button[data-slide]');
            buttons.forEach((button, buttonIndex) => {
                button.className = '';
                button.innerHTML = `<span class="visually-hidden">Testimonial</span> ${buttonIndex + 1}`;
            });
            buttons[new_current].className = 'current slide-indicator';
            buttons[new_current].innerHTML = `
            <span class="visually-hidden">Testimonial</span> ${new_current + 1}
            <span class="visually-hidden">(Current Item)</span>
          `;
        }

        index = new_current;
    },

    // Function to advance to the next slide
    next(announceItem = false) {
        const length = slides.length;
        let new_current = index + 1;

        if (new_current === length) {
            new_current = 0;
        }

        Testimonials.goto(new_current, false, 'prev', announceItem);

        if (settings.animate) {
            timer = setTimeout(() => Testimonials.next(), 5000);
        }
    },

    // Function to advance to the previous slide
    prev(announceItem = false) {
        const length = slides.length;
        let new_current = index - 1;

        if (new_current < 0) {
            new_current = length - 1;
        }

        Testimonials.goto(new_current, false, 'next', announceItem);
    },

    // Function to stop the animation
    stop() {
        clearTimeout(timer);
        settings.animate = false;
        animationSuspended = false;
        const actionbutton = carousel.querySelector('[data-action]');
        actionbutton.innerHTML = '<span class="visually-hidden">Start Animation </span>▶';
        actionbutton.setAttribute('data-action', 'start');
    },

    // Function to start the animation
    start() {
        settings.animate = true;
        animationSuspended = false;
        timer = setTimeout(() => Testimonials.next(), 5000);
        const databutton = carousel.querySelector('[data-action]');
        databutton.innerHTML = '<span class="visually-hidden">Stop Animation </span>⏸';
        databutton.setAttribute('data-action', 'stop');
    },

    // Function to suspend the animation
    suspend() {
        if (settings.animate) {
            clearTimeout(timer);
            settings.animate = false;
            animationSuspended = true;
        }
    },
};

export default Testimonials;
